import React, { useMemo } from 'react'
import { Card, Text, Stack } from '@mantine/core'
import { ParentSize } from '@visx/responsive'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import { MetricParams } from 'plugin-product-frac-ui/shared/registries/metrics-registry'
import useGetAggregatedStages from '../../../services/kpis/hooks/useGetAggregatedStages'
import EmptySearchChart from '../EmptySearchChart'
import StagesBarChart from './StagesBarChart'
import { ChartDatum } from './types'

type ChartMetricsCardProps = {
    params: MetricParams
}

const ChartMetricsCard: React.FC<ChartMetricsCardProps> = ({ params }) => {
    const { data, loading, error } = useGetAggregatedStages({
        params,
        lookbackDays: 7
    })

    const chartApiData = useMemo<Record<string, number>>(() => {
        return Object.entries(data?.total_per_24h || {}).reduce<Record<string, number>>((acc, [timestamp, value]) => {
            const date = new Date(Number(timestamp))
            const dateKey = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
            acc[dateKey] = Number(value)

            return acc
        }, {})
    }, [data?.total_per_24h])

    const chartData: ChartDatum[] = useMemo(() => {
        return Object.entries(chartApiData)
            .sort(([dateA], [dateB]) => {
                const [dayA, monthA, yearA] = dateA.split('/').map(Number)
                const [dayB, monthB, yearB] = dateB.split('/').map(Number)

                const parsedA = new Date(yearA, monthA - 1, dayA).getTime()
                const parsedB = new Date(yearB, monthB - 1, dayB).getTime()

                return parsedA - parsedB
            })
            .map(([dateString, value]) => {
                const [day, month, year] = dateString.split('/')
                const date = new Date(Number(year), Number(month) - 1, Number(day))

                const monthLabel = date.toLocaleString('en-US', { month: 'short' }).toLowerCase()
                const safeValue = (data?.count ?? 0) === 0 ? 0 : value

                return {
                    date: dateString,
                    value: safeValue,
                    label: `${monthLabel}/${day}`
                }
            })
    }, [chartApiData, data?.count])

    const shouldRenderEmpty = !loading && (!!error || chartData.length === 0)

    if (loading) {
        return (
            <ChartCardContainer>
                <Card radius="md" p={0}>
                    <Stack spacing="sm" align="center">
                        <Text size="sm">{i18n('Loading...')}</Text>
                    </Stack>
                </Card>
            </ChartCardContainer>
        )
    }

    return (
        <ChartCardContainer>
            <Card radius="md" p={0}>
                <ChartWrapper>
                    {shouldRenderEmpty ? (
                        <EmptySearchChart />
                    ) : (
                        <>
                            <TextWrapper>
                                <Stack align="center" spacing="0">
                                    <Text
                                        align="center"
                                        size="lg"
                                        lh={1}
                                        weight={500}
                                        color="var(--mantine-text-default)"
                                    >
                                        {i18n('Stages completed')}
                                    </Text>
                                    <Text
                                        align="center"
                                        size="lg"
                                        lh={1}
                                        weight={500}
                                        color="var(--mantine-text-default)"
                                    >
                                        {i18n('per day')}
                                    </Text>
                                    <Text
                                        align="center"
                                        size="xs"
                                        lh={2}
                                        weight={500}
                                        color="var(--mantine-text-subtle)"
                                    >
                                        {i18n('IN THE LAST 7 DAYS')}
                                    </Text>
                                </Stack>
                            </TextWrapper>

                            <ChartContainer>
                                <ParentSize>
                                    {({ width, height }) => {
                                        const chartHeight = Math.max(1, Math.floor(height))

                                        return <StagesBarChart width={width} height={chartHeight} data={chartData} />
                                    }}
                                </ParentSize>
                            </ChartContainer>
                        </>
                    )}
                </ChartWrapper>
            </Card>
        </ChartCardContainer>
    )
}

const ChartCardContainer = styled.div`
    position: relative;
    inset: auto;
    transform: none;
    width: 100%;
    overflow: visible;
`

const ChartWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    min-height: 130px;
    overflow: visible;

    @media (max-width: 1200px) {
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: 0.5rem;
        min-height: 72px;
        padding: 6px;
    }
`

const ChartContainer = styled.div`
    order: 2;
    flex: 1 1 auto;
    height: 130px;
    min-width: 200px;
    max-width: 100%;
    overflow: visible;

    @media (max-width: 1200px) {
        order: 1;
        flex: 0 0 auto;
        height: 72px;
        min-width: 0;
        width: 100%;
        padding: 6px;
    }
`

const TextWrapper = styled.div`
    order: 1;
    flex-shrink: 0;

    @media (max-width: 1200px) {
        order: 2;
        display: block;
        width: 100%;
        visibility: visible;
        opacity: 1;
    }
`

export default ChartMetricsCard
